/**
 * DealReports.js — per-deal cash and return, read from the general ledger
 * ----------------------------------------------------------------------
 * Two pages under one route family:
 *
 *   /deal-reports         the whole book: the fund's totals, MOIC against
 *                         amount invested for every deal in the list, and the
 *                         list itself as a plain sortable table — or, under
 *                         "By strategy", every deal of one kind added together
 *   /deal-reports/<id>    one deal on its own page, in six tabs: Summary,
 *                         Investors, Cash curve, Tranches, Activity, Documents
 *
 * The usability review of 23 October 2026 (items 4–8 and 21–26) is what put
 * them on two pages; the pieces are in `./dealReports/`, one file each, and
 * the helpers the tests pin are re-exported from here.
 *
 * The honesty rules the page has always enforced are unchanged, and each
 * lives with the piece that applies it:
 *
 *   - A deal whose sales outrun its loaded cost basis reports a return it has
 *     not made; the API says so per deal (`basis_complete`), and the page marks
 *     the deal provisional and strikes its net cash through rather than
 *     printing it as a return.
 *   - A figure the code cannot stand behind reads n/a with the reason, never
 *     0.00 (CLAUDE.md 0.30), and is never ranked or drawn as if it were one.
 *   - A deal in more than one currency reads in dollars converted at each
 *     date's fixing, or at face value with a warning saying so — never a
 *     krona-and-dollar sum labelled with one of its currencies (D-136).
 *   - Nothing on screen names a database row or a ticket (0.2): the ledger's
 *     own descriptions are cleaned for display (`./dealReports/text.js`).
 *
 * Data: GET /api/v1/reports/deal-cash-series/?deal=all   every figure here
 *       GET /api/v1/reports/deal-investor-waterfall/?deal=<id>
 *                                                        the Investors tab
 *       GET /api/v1/reports/deal-activity/?deal=<id>     the Activity tab
 *       GET /api/v1/securities/notices/?deal=<id>        the Documents tab
 *
 * The first is read through `useCachedGet`, so the page shows the figures it
 * last read at once, with a badge saying when, and brings them up to date in
 * the background (review item 4: it used to sit on "Reading the ledger…" for
 * ten seconds).
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Card, EmptyState, FilterPill, LoadingSpinner, PageHeader } from "../components/ui";
import { ExportCsvButton, FreshnessBadge, PdfButton, ToolRow } from "../components/PageTools";
import useCachedGet from "../components/useCachedGet";
import { dealReportPath } from "../components/links";
import theme from "../components/theme";

import {
  DEAL_STATUS_FILTERS,
  dealListSubtitle,
  filterDealsByStatus,
  neighbours,
  noMatchText,
  readListState,
  searchDeals,
  searchStrategies,
  searchWords,
  statusKeyOf,
  writeListState,
} from "./dealReports/listing";
import { nextSort, sortDeals } from "./dealReports/stats";
import { FundSummary } from "./dealReports/FundSummary";
import { PortfolioChart } from "./dealReports/PortfolioChart";
import { DEAL_CSV_COLUMNS, DealTable, DealTableFooter } from "./dealReports/DealTable";
import { StrategyDetail, StrategyPicker } from "./dealReports/Strategy";
import { DealDetail, NotFound } from "./dealReports/DealDetail";
import { clearActivityCache } from "./dealReports/Activity";
import { clearNoticeCache } from "./dealReports/Documents";
import { clearWaterfallCache } from "./dealReports/Investors";
import "./DealReports.css";

// What the tests pin, from the files that now hold it.
export {
  STAT_COLUMNS,
  VALUE_BASIS_COLUMNS,
  nextSort,
  sortDeals,
  sortValue,
  statText,
  statTitle,
  statTone,
  unrankedCount,
} from "./dealReports/stats";
export {
  DEAL_STATUS_FILTERS,
  LIST_STATE_KEY,
  dealListSubtitle,
  dealSearchText,
  filterDealsByStatus,
  matchesSearch,
  neighbours,
  noMatchText,
  readListState,
  searchDeals,
  searchStrategies,
  statusKeyOf,
  writeListState,
} from "./dealReports/listing";
export { readerText, withoutRowIds } from "./dealReports/text";
export {
  FUNDING_ROUNDS_MIN_TRANCHES,
  amountsOf,
  boughtText,
  coverText,
  figureLines,
  lowerFirst,
  nativeText,
  proceedsHeading,
  registerText,
  showFundingRounds,
  singleTrancheText,
  soldBeforeTiles,
  usdText,
  windowDates,
  windowFigures,
  windowTitle,
} from "./dealReports/FundingRounds";
export { fundingMarkers } from "./dealReports/Curve";
export {
  PnlBridge,
  bridgeMoney,
  bridgeNativeText,
  bridgeTitle,
  fixingText,
  lineMovements,
  moicParts,
  moicText,
  movementsText,
  sensitivityText,
} from "./dealReports/PnlBridge";
export { DEAL_PDF_ENDPOINT, downloadDealPdf, pdfError, pdfFilename } from "./dealReports/pdf";
export { Legs, intercompanyLabel, otherLegsNote, sideLabel } from "./dealReports/Activity";
export { repointedText } from "./dealReports/DealDetail";

const SERIES_URL = "/reports/deal-cash-series/";
const SERIES_PARAMS = { deal: "all" };

// The page's own order: the most cash tied up at once first. It is what the
// list opens on and what Previous and Next walk when the list was not seen.
export const byExposure = (deals) =>
  [...(deals || [])].sort((a, b) => Number(a.summary.max_cash_exposure) - Number(b.summary.max_cash_exposure));

function SearchBox({ value, onChange }) {
  return (
    <input
      type="search"
      className="dr-search"
      aria-label="Search deals"
      placeholder="Search deal, company, ticker or strategy…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      // Escape clears, as it does in the browser's own find bar.
      onKeyDown={(e) => {
        if (e.key === "Escape") onChange("");
      }}
    />
  );
}

function ListPage({ data, deals, freshness, openDeal }) {
  // Where the list was left, for this tab: the same filter, search and
  // ranking when the reader comes back from a deal (listing.js).
  const [saved] = useState(readListState);
  const [mode, setMode] = useState(saved.mode);
  // Defaults to the deals still running, which is what Brian asked for.
  const [statusFilter, setStatusFilter] = useState(saved.statusFilter);
  const [query, setQuery] = useState(saved.query);
  // null is the page's own order; a click on a heading ranks by it, and a
  // third click comes back here.
  const [sort, setSort] = useState(saved.sort);
  const [selectedStrategy, setSelectedStrategy] = useState(saved.strategy);

  const searched = useMemo(() => searchDeals(deals, query), [deals, query]);
  const searching = searchWords(query).length > 0;
  const visibleDeals = useMemo(
    () => sortDeals(filterDealsByStatus(searched, statusFilter), sort),
    [searched, statusFilter, sort]
  );
  // Counted after the search, so while one is typed each pill says how many
  // of its deals match — which is how a match on the Closed tab is seen from
  // the Active one.
  const statusCounts = useMemo(() => {
    const counts = { active: 0, closed: 0, other: 0, all: searched.length };
    searched.forEach((d) => {
      counts[statusKeyOf(d)] += 1;
    });
    return counts;
  }, [searched]);

  // Deepest exposure first, with the untagged group last: it is not a
  // strategy, it is the deals somebody still has to tag.
  const strategies = useMemo(
    () =>
      [...(data.strategies || [])].sort((a, b) => {
        if (!a.strategy !== !b.strategy) return a.strategy ? -1 : 1;
        return Number(a.summary.max_cash_exposure) - Number(b.summary.max_cash_exposure);
      }),
    [data]
  );
  const visibleStrategies = useMemo(() => searchStrategies(strategies, query), [strategies, query]);
  const group = visibleStrategies.find((g) => g.strategy === selectedStrategy) || visibleStrategies[0] || null;

  const order = useMemo(() => visibleDeals.map((d) => d.deal.id), [visibleDeals]);
  useEffect(() => {
    writeListState({
      mode,
      statusFilter,
      query,
      sort,
      strategy: group ? group.strategy : selectedStrategy,
      ...(mode === "deal" ? { order } : {}),
    });
  }, [mode, statusFilter, query, sort, group, selectedStrategy, order]);

  // From the strategy table: Previous and Next then walk that strategy.
  const openFromStrategy = (id) => {
    writeListState({ order: (group?.deals || []).map((d) => d.id) });
    openDeal(id);
  };

  const scopeText = searching
    ? `the ${statusFilter === "all" ? "" : `${statusFilter} `}deals matching “${query.trim()}”`
    : statusFilter === "all"
    ? "every deal"
    : `the ${statusFilter} deals`;

  return (
    <div className="dr-page">
      <PageHeader
        title="Deal Reports"
        subtitle="Cash out, cash back and the return on every deal, read from the general ledger"
        action={
          <ToolRow>
            {freshness}
            <PdfButton />
          </ToolRow>
        }
      />

      <FundSummary data={data} deals={deals} />

      {/* ── What the page is reading: one deal, or a kind of deal ── */}
      <div className="dr-controls no-print">
        <FilterPill label="By deal" count={deals.length} active={mode === "deal"} onClick={() => setMode("deal")} />
        <FilterPill label="By strategy" count={strategies.length} active={mode === "strategy"} onClick={() => setMode("strategy")} />
        <span className="dr-controls-note">
          {mode === "deal"
            ? "One investment at a time, in the currency it was written in."
            : "Every deal of one kind added together, in dollars at each date's fixing."}
        </span>
      </div>

      {/* ── Which deals: running, finished, or every one, and the search ──
          The pills only by deal. A strategy's curve is computed over every
          deal of that kind, so filtering its member list would leave a total
          that could no longer be read back to the deals beneath it. The
          search stays in both, in the same place, because it narrows the
          strategies too. */}
      <div className="dr-controls no-print">
        {mode === "deal" &&
          DEAL_STATUS_FILTERS.map((f) => (
            <FilterPill
              key={f.key}
              label={f.label}
              count={statusCounts[f.key]}
              active={statusFilter === f.key}
              onClick={() => setStatusFilter(f.key)}
            />
          ))}
        <div className="dr-search-wrap">
          <SearchBox value={query} onChange={setQuery} />
        </div>
        {mode === "deal" && statusCounts.other > 0 && (
          <span className="dr-controls-note">
            {statusCounts.other === 1
              ? "1 deal is neither Active nor Closed"
              : `${statusCounts.other} deals are neither Active nor Closed`}
            {" — "}
            {[
              ...new Set(
                searched
                  .filter((d) => statusKeyOf(d) === "other")
                  .map((d) => (d.deal.status || "").trim() || "no status recorded")
              ),
            ].join(", ")}
            {". Only All shows them."}
          </span>
        )}
      </div>

      {mode === "deal" ? (
        <>
          {visibleDeals.length > 0 && (
            <PortfolioChart deals={visibleDeals} allDeals={deals} onOpenDeal={openDeal} scopeText={scopeText} />
          )}
          <Card
            title="Deals"
            subtitle={dealListSubtitle({
              shown: visibleDeals.length,
              total: deals.length,
              statusFilter,
              query: searching ? query.trim() : "",
            })}
            action={<ExportCsvButton filename="deal-reports" columns={DEAL_CSV_COLUMNS} rows={visibleDeals} />}
            noPad
          >
            {visibleDeals.length ? (
              <>
                <DealTable
                  deals={visibleDeals}
                  sort={sort}
                  onSort={(key) => setSort((current) => nextSort(current, key))}
                  onOpen={openDeal}
                  lastDealId={saved.lastDealId}
                />
                <DealTableFooter deals={visibleDeals} sort={sort} />
              </>
            ) : searching ? (
              <div style={{ padding: theme.space.lg }}>
                <EmptyState
                  message={noMatchText({ query: query.trim(), statusFilter, elsewhere: searched.length })}
                  icon="◌"
                  action={
                    searched.length ? (
                      <Button variant="secondary" size="sm" onClick={() => setStatusFilter("all")}>
                        Show {searched.length === 1 ? "it" : `all ${searched.length}`}
                      </Button>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={() => setQuery("")}>
                        Clear the search
                      </Button>
                    )
                  }
                />
              </div>
            ) : (
              <div style={{ padding: theme.space.lg }}>
                <EmptyState
                  message={`No ${statusFilter} deal has posted cash movement. The other ${deals.length} are on the All tab.`}
                  icon="◌"
                />
              </div>
            )}
          </Card>
        </>
      ) : (
        <div className="dr-split">
          <Card
            title="Strategies"
            subtitle={
              searching
                ? `${visibleStrategies.length} of ${strategies.length} match “${query.trim()}”, by name or by a deal in them`
                : `${strategies.length} with cash movement`
            }
            noPad
          >
            {visibleStrategies.length ? (
              <StrategyPicker groups={visibleStrategies} selected={group ? group.strategy : null} onSelect={setSelectedStrategy} />
            ) : (
              <div style={{ padding: theme.space.lg }}>
                <EmptyState
                  message={`No strategy, and no deal in one, matches “${query.trim()}”.`}
                  icon="◌"
                  action={
                    <Button variant="secondary" size="sm" onClick={() => setQuery("")}>
                      Clear the search
                    </Button>
                  }
                />
              </div>
            )}
          </Card>
          <div>{group && <StrategyDetail group={group} onOpenDeal={openFromStrategy} />}</div>
        </div>
      )}
    </div>
  );
}

function DealPage({ dealId, deals, freshness }) {
  const deal = deals.find((d) => String(d.deal.id) === String(dealId)) || null;
  const id = deal ? deal.deal.id : null;
  useEffect(() => {
    if (id !== null) writeListState({ lastDealId: id });
  }, [id]);
  const steps = useMemo(
    () => neighbours(id, readListState().order, byExposure(deals).map((d) => d.deal.id)),
    [id, deals]
  );
  const names = useMemo(() => new Map(deals.map((d) => [d.deal.id, d.deal.name])), [deals]);
  if (!deal) return <NotFound />;
  return <DealDetail deal={deal} steps={steps} nameOf={(x) => names.get(x) || ""} freshness={freshness} />;
}

export default function DealReports() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { data, savedAt, refreshing, error, reload } = useCachedGet(SERIES_URL, { params: SERIES_PARAMS });

  const deals = useMemo(() => byExposure(data?.deals), [data]);

  const refresh = () => {
    // The Investors, Activity and Documents tabs are read once per visit; a
    // refresh asks for those again too.
    clearActivityCache();
    clearNoticeCache();
    clearWaterfallCache();
    reload();
  };
  const freshness = <FreshnessBadge savedAt={savedAt} refreshing={refreshing} error={error} onRefresh={refresh} />;
  const openDeal = (id) => {
    writeListState({ lastDealId: id });
    navigate(dealReportPath(id));
  };

  if (!data) {
    if (error) return <EmptyState message={error} icon="⚠" />;
    return <LoadingSpinner message="Reading the ledger…" />;
  }
  if (!deals.length) {
    return (
      <>
        <PageHeader title="Deal Reports" subtitle="Cash out, cash back and the return on every deal" action={freshness} />
        <EmptyState
          message="No deal has posted cash movement yet. A deal appears here once cash moved for it is posted to the ledger."
          icon="◌"
        />
      </>
    );
  }
  if (dealId !== undefined) return <DealPage dealId={dealId} deals={deals} freshness={freshness} />;
  return <ListPage data={data} deals={deals} freshness={freshness} openDeal={openDeal} />;
}
